import React from 'react';

import { ForwardToInbox as ForwardToInboxIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  inputBaseClasses,
  Typography,
  tablePaginationClasses,
} from '@mui/material';

import { DateFormatPatterns, SELECT_ALL_OPTION } from '@clearverify/constants';
import { formatDate } from '@clearverify/utils';

import { EmailActivityItemInfoFragment, EmailDeliveryStatus } from 'src/api';
import { Table, TableColumn, TableFilter, TableTopAction } from 'src/components/table';
import { ModalId } from 'src/constants/modals';
import { spacingStyling } from 'src/constants/spacing';
import { usePagination } from 'src/hooks/common';
import { useModalV2 } from 'src/hooks/shared';
import { ResubscribeEmailModal } from 'src/modals/emailTracker';
import { SxProp } from 'src/types/mui';
import { enumToSelectOptions } from 'src/utils/common';

import { EMAIL_DELIVERY_STATUS_LABEL_MAP, EmailTrackerTableFilterId } from '../constants';

interface EmailTrackerTableProps {
  emailActivityList: EmailActivityItemInfoFragment[];
  loading: boolean;
  refetchData: () => Promise<void>;
}

export const EmailTrackerTable: React.FC<EmailTrackerTableProps> = ({
  emailActivityList,
  loading,
  refetchData,
}) => {
  const { openModal } = useModalV2();

  const pagination = usePagination();

  const topActions = React.useMemo((): TableTopAction[] => {
    return [
      {
        actionName: 'Resubscribe Email',
        show: true,
        icon: <ForwardToInboxIcon />,
        onAction: () => {
          openModal(ResubscribeEmailModal, { id: ModalId.ResubscribeEmail });
        },
        render() {
          return (
            <Button
              key={this.actionName}
              variant="contained"
              title={this.actionName}
              onClick={this.onAction}
              startIcon={this.icon}
            >
              {this.actionName}
            </Button>
          );
        },
      },
    ];
  }, [openModal]);

  return (
    <Box sx={tableContainerStyles}>
      <Table
        dataSource={emailActivityList}
        rowCount={emailActivityList.length}
        columns={columns}
        filters={tableFilters}
        topActions={topActions}
        pagination={pagination}
        loading={loading}
        onRefresh={refetchData}
      />
    </Box>
  );
};

const columns: Array<TableColumn<EmailActivityItemInfoFragment>> = [
  {
    title: 'Message',
    key: 'message',
    render: (_valueByDataPath, emailActivity) => {
      return (
        <React.Fragment>
          <Typography variant="body1" fontWeight="bold">
            To: {emailActivity.to_email}
          </Typography>
          <Typography variant="body1">{emailActivity.subject}</Typography>
        </React.Fragment>
      );
    },
  },
  {
    title: 'Status',
    key: 'status',
    render: (_valueByDataPath, emailActivity) => {
      return renderEmailDeliveryStatusChip(emailActivity.status);
    },
  },
  {
    title: 'Last Event Received',
    key: 'lastEventReceived',
    render: (_valueByDataPath, emailActivity) => {
      return formatDate(emailActivity.last_event_time, DateFormatPatterns.fullDateWithTime);
    },
  },
  {
    title: 'Opens',
    key: 'opens',
    render: (_valueByDataPath, emailActivity) => {
      return emailActivity.opens_count;
    },
  },
  {
    title: 'Clicks',
    key: 'clicks',
    render: (_valueByDataPath, emailActivity) => {
      return emailActivity.clicks_count;
    },
  },
];

const tableFilters: TableFilter[] = [
  {
    id: EmailTrackerTableFilterId.toEmail,
    type: 'text',
    fieldProps: {
      label: 'Recipient email',
    },
  },
  {
    id: EmailTrackerTableFilterId.status,
    type: 'select',
    initialValue: SELECT_ALL_OPTION.value,
    fieldProps: {
      label: 'Status',
      options: [
        SELECT_ALL_OPTION,
        ...enumToSelectOptions(EmailDeliveryStatus, EMAIL_DELIVERY_STATUS_LABEL_MAP),
      ],
    },
  },
];

const renderEmailDeliveryStatusChip = (status: EmailDeliveryStatus) => {
  return (
    <Chip
      sx={chipStyles[status]}
      label={EMAIL_DELIVERY_STATUS_LABEL_MAP[status]}
      size="small"
      variant="filled"
    />
  );
};

const chipStyles = {
  [EmailDeliveryStatus.Delivered]: theme => ({
    backgroundColor: theme.palette.chip?.success?.main,
    color: theme.palette.chip?.success?.contrastText,
    borderColor: theme.palette.chip?.success?.contrastText,
  }),
  [EmailDeliveryStatus.NotDelivered]: theme => ({
    backgroundColor: theme.palette.chip?.error?.main,
    color: theme.palette.chip?.error?.contrastText,
    borderColor: theme.palette.chip?.error?.contrastText,
  }),
  [EmailDeliveryStatus.Processing]: theme => ({
    backgroundColor: theme.palette.chip?.warning?.main,
    color: theme.palette.chip?.warning?.contrastText,
    borderColor: theme.palette.chip?.warning?.contrastText,
  }),
} satisfies Record<EmailDeliveryStatus, SxProp>;

const tableContainerStyles: SxProp = {
  padding: spacingStyling.allTables,
  [`.${tablePaginationClasses.root}`]: {
    [`.${tablePaginationClasses.displayedRows}, .${tablePaginationClasses.actions}`]: {
      display: 'none',
    },
    [`.${inputBaseClasses.root}`]: {
      marginRight: 0,
    },
  },
};
